import React, { useState, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import FilterModuleDateRange from '@/components/filters/FilterModuleDateRange';
import { notificationService } from '@core/notifications/NotificationService';
import { getMenuData } from '@core/data/menuData';
import AppCard from '@/components/appcard/AppCard';
import { lineTrackerService } from '@/services/lineTrackerService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';

interface FilterArgs {
    selectedShopId: string;
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
}

const LineTrackerRelations: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<{ name: string; data: { x: string; y: number }[] }[]>([]);

    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await lineTrackerService.loadRelations(args.deviceId, args.moduleId, args.start, args.end);

                const sorted = data.sort((a, b) => a.elementName.localeCompare(b.elementName));

                const newSeries = [
                    {
                        name: t('value'),
                        data: sorted.map((x) => ({ x: x.elementName, y: x.count }))
                    }
                ];

                setSeries(newSeries);
                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load line tracker relations:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-view-headline" />}
                iconSize="20px"
                title={`${t('lineTracker')} ${t('relations')}`}
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/linetracker/relations' }}
                lastUpdate={lastUpdate}
                t={t}
            />

            <Container maxWidth={false} sx={{ pb: 3 }}>
                <FilterModuleDateRange ref={filterRef} onLoadData={loadData} forType="lineTracker" />

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={t('relations')}
                        icon="view_headline"
                        iconClass=""
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            series={series}
                            height={550}
                            chartId="line-tracker-relations"
                            xAxisType="category"
                            dataLabelsEnabled={false}
                            yAxisForceNiceScale={true}
                            tooltipShared={false}
                            tooltipIntersect={true}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

LineTrackerRelations.displayName = 'LineTrackerRelations';

export default LineTrackerRelations;
